import React from "react";
import type { ArtBriefing } from "@/api/templates";

/** Perguntas de briefing de arte — múltipla escolha pra usuário leigo, última
 *  opção "Outro" abre texto. Direciona roteamento + prompt + bloqueio.
 *
 *  Fonte ÚNICA das perguntas: usado tanto na criação de tarefa (NovaTarefa)
 *  quanto no modal de reabrir (Resultado → ReopenTaskButton). Não duplicar. */
export const ART_BRIEFING_QUESTIONS: {
  key: keyof ArtBriefing;
  label: string;
  hint?: string;
  options: { v: string; l: string }[];
  /** Mostra essa pergunta só pra certos templates (recebe a key). Sem isso,
   *  a pergunta aparece sempre. */
  showFor?: (templateKey?: string) => boolean;
}[] = [
  {
    key: "formato",
    label: "Qual o formato?",
    hint: "Feed sai quadrado; Stories sai vertical 9:16",
    showFor: (k) =>
      !!k && (k.startsWith("briefing_criativo") || k.startsWith("post_instagram")),
    options: [
      { v: "feed", l: "Feed (quadrado)" },
      { v: "stories", l: "Stories (vertical)" },
    ],
  },
  {
    key: "logo",
    label: "Sua logo deve aparecer?",
    options: [
      { v: "sim", l: "Sim" },
      { v: "nao", l: "Não" },
    ],
  },
  {
    key: "objetivo",
    label: "Pra que é essa arte?",
    options: [
      { v: "autoridade", l: "Mostrar autoridade" },
      { v: "vender", l: "Vender / converter" },
      { v: "engajar", l: "Engajar / educar" },
      { v: "anuncio", l: "Anúncio pago" },
    ],
  },
  {
    key: "clima",
    label: "Qual o clima visual?",
    options: [
      { v: "fotografico", l: "Fotográfico e épico" },
      { v: "minimalista", l: "Limpo e minimalista" },
      { v: "vibrante", l: "Colorido e vibrante" },
      { v: "ilustrado", l: "Ilustrado / conceitual" },
    ],
  },
];

const pillStyle = (selected: boolean): React.CSSProperties => ({
  padding: "8px 14px",
  borderRadius: 999,
  border: selected ? "1px solid var(--nd-orange)" : "1px solid var(--wf-ink-5)",
  background: selected ? "#fff8ef" : "var(--wf-paper)",
  color: selected ? "var(--nd-orange)" : "var(--wf-ink-2)",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "var(--wf-font-body)",
});

export type ArtBriefingQuestionsProps = {
  /** Valor atual do briefing. */
  value: ArtBriefing;
  /** Atualiza o briefing inteiro (mantém os outros campos). */
  onChange: (v: ArtBriefing) => void;
  /** Quando true, envolve as perguntas num card laranja com cabeçalho
   *  ("Como você quer a arte?") — usado no fluxo de criação (NovaTarefa).
   *  Quando false (padrão), renderiza só as perguntas, sem moldura — o modal
   *  de reabrir (Resultado) já fornece o enquadramento. */
  framed?: boolean;
  /** Key do template atual — pra mostrar perguntas condicionais (ex: formato,
   *  só em criativo/post). */
  templateKey?: string;
};

/** Renderiza as perguntas + o campo livre "específico". Não envolve em card por
 *  padrão (use `framed` pra isso). */
function ArtBriefingQuestions({
  value,
  onChange,
  framed = false,
  templateKey,
}: ArtBriefingQuestionsProps) {
  const fields = (
    <>
      {ART_BRIEFING_QUESTIONS.filter((q) => !q.showFor || q.showFor(templateKey)).map((q) => {
        const cur = value[q.key];
        const isCustom = !!cur && !q.options.some((o) => o.v === cur);
        return (
          <div key={q.key} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>{q.label}</span>
            {q.hint && (
              <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{q.hint}</span>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {q.options.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  style={pillStyle(cur === o.v)}
                  onClick={() => onChange({ ...value, [q.key]: o.v })}
                >
                  {o.l}
                </button>
              ))}
              <button
                type="button"
                style={pillStyle(isCustom)}
                onClick={() => onChange({ ...value, [q.key]: isCustom ? "" : " " })}
              >
                Outro
              </button>
            </div>
            {isCustom && (
              <input
                autoFocus
                value={cur === " " ? "" : cur}
                onChange={(e) => onChange({ ...value, [q.key]: e.target.value || " " })}
                placeholder="Descreva..."
                style={{
                  background: "var(--wf-paper)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  fontFamily: "var(--wf-font-body)",
                  color: "var(--wf-ink)",
                  outline: "none",
                }}
              />
            )}
          </div>
        );
      })}

      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
          Tem algo específico que PRECISA aparecer?{" "}
          <span style={{ color: "var(--wf-ink-3)", fontWeight: 500 }}>(opcional)</span>
        </span>
        <input
          value={value.especifico ?? ""}
          onChange={(e) => onChange({ ...value, especifico: e.target.value })}
          placeholder="Ex: uma estátua de pedra, um produto, um cenário de praia..."
          style={{
            background: "var(--wf-paper)",
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            fontFamily: "var(--wf-font-body)",
            color: "var(--wf-ink)",
            outline: "none",
          }}
        />
      </div>
    </>
  );

  if (!framed) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{fields}</div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: 16,
        borderRadius: 12,
        background: "rgba(255,112,0,0.04)",
        border: "1px solid var(--wf-ink-5)",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wf-ink)" }}>
        Como você quer a arte?
        <span style={{ color: "var(--wf-ink-3)", fontWeight: 500 }}> (rápido, só clicar)</span>
      </div>
      {fields}
    </div>
  );
}

export default ArtBriefingQuestions;
export { ArtBriefingQuestions };
